import { SyntheticEvent, useCallback, useEffect, useState } from 'react'
// @mui
import Stack from '@mui/material/Stack'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Unstable_Grid2'
import InputAdornment from '@mui/material/InputAdornment'
// components
import Iconify from 'src/components/iconify'
import DateTimeRangePicker from 'src/components/date-time-range-picker'

import useEvents from 'src/service/events.service'
import useDebounce from 'src/hooks/use-debounce'
import { OptionType } from 'src/components/hook-form/rhf-autocomplete'
import {
  Autocomplete,
  Checkbox,
  InputLabel,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material'
import { IOrderTableFilterValue, IOrderTableFilters } from 'src/types/orders'
import useUsers from 'src/service/users.service'
import { getCategoriesToAutocomplete } from 'src/service/categories.service'

// ----------------------------------------------------------------------

const paymentTypes: any = [
  { value: 'money', label: 'Dinheiro' },
  { value: 'credit', label: 'Cartão de Crédito' },
  { value: 'debit', label: 'Cartão de Débito' },
  { value: 'pix', label: 'Pix' },
  { value: 'card', label: 'CashLess' },
]

type Props = {
  filters: IOrderTableFilters
  onFilters: (name: string, value: IOrderTableFilterValue) => void
  onExportExcel?: () => void
  onExportPDF?: () => void
  hasData?: boolean
}

export default function OrderTableToolbar({ filters, onFilters, onExportExcel, onExportPDF, hasData }: Props) {
  // Estado local para terminalSerial com debounce
  const [terminalSerialInput, setTerminalSerialInput] = useState(filters.terminalSerial || '')
  const debouncedTerminalSerial = useDebounce(terminalSerialInput, 800)

  // Atualiza o filtro quando o valor com debounce mudar
  useEffect(() => {
    if (debouncedTerminalSerial !== filters.terminalSerial) {
      onFilters('terminalSerial', debouncedTerminalSerial)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTerminalSerial])

  // Sincroniza o input local APENAS quando os filtros são resetados externamente
  useEffect(() => {
    if (filters.terminalSerial === '' && terminalSerialInput !== '') {
      setTerminalSerialInput('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.terminalSerial])

  const handleFilterDateRange = useCallback(
    (start: Date | null, end: Date | null) => {
      onFilters('dateStart', start)
      onFilters('dateEnd', end)
    },
    [onFilters],
  )

  const handleFilterPaymentType = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      onFilters(
        'paymentType',
        typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value,
      )
    },
    [onFilters],
  )

  const handleFilterTerminalSerial = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setTerminalSerialInput(event.target.value)
  }, [])

  const hasEvent = !!filters.event?.value

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={1.5} alignItems='flex-start'>
        {/* Período (em destaque, ocupando largura total) */}
        <Grid xs={12}>
          <DateTimeRangePicker
            label='Período'
            size='small'
            startDate={filters.dateStart}
            endDate={filters.dateEnd}
            onChange={handleFilterDateRange}
          />
        </Grid>

        {/* Evento */}
        <Grid xs={12} md={hasEvent ? 5 : 7}>
          <SelectEvents onFilters={onFilters} filters={filters} />
        </Grid>

        {/* Categoria */}
        {hasEvent && (
          <Grid xs={12} sm={6} md={3}>
            <SelectCategory onFilters={onFilters} filters={filters} />
          </Grid>
        )}

        {/* Meio de Pagamento */}
        <Grid xs={12} sm={6} md={hasEvent ? 4 : 5}>
          <FormControl size='small' sx={{ width: '100%' }}>
            <InputLabel>Meio de pagamento</InputLabel>
            <Select
              multiple
              value={filters.paymentType}
              onChange={handleFilterPaymentType}
              input={<OutlinedInput label='Meio de pagamento' />}
              renderValue={(selected) =>
                selected.map((value) => JSON.parse(value).label).join(', ')
              }
              MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
            >
              {paymentTypes.map((option: any) => (
                <MenuItem key={option.value} value={JSON.stringify(option)}>
                  <Checkbox
                    disableRipple
                    size='small'
                    checked={
                      !!filters.paymentType.find(
                        (cat: any) => JSON.parse(cat).value === option.value,
                      )
                    }
                  />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Vendedores */}
        <Grid xs={12} md={8}>
          <SelectVendedores onFilters={onFilters} filters={filters} />
        </Grid>

        {/* Terminal Serial */}
        <Grid xs={12} md={4}>
          <TextField
            label='Terminal'
            value={terminalSerialInput}
            onChange={handleFilterTerminalSerial}
            placeholder='Serial do terminal'
            fullWidth
            size='small'
            helperText='Busca automática ao parar de digitar'
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Iconify
                    icon='solar:smartphone-bold'
                    width={16}
                    sx={{ color: 'text.disabled' }}
                  />
                </InputAdornment>
              ),
            }}
            FormHelperTextProps={{
              sx: { mx: 0, fontSize: 11, color: 'text.secondary' },
            }}
          />
        </Grid>
      </Grid>

      {/* Botões de Exportar */}
      {(onExportExcel || onExportPDF) && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.25}
          justifyContent='flex-end'
          sx={{ width: 1, mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}
        >
          {onExportPDF && (
            <Button
              variant='outlined'
              size='small'
              startIcon={<Iconify icon='solar:document-bold' width={16} />}
              onClick={onExportPDF}
              disabled={!hasData}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                borderColor: '#B71C1C',
                color: '#B71C1C',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 1.5,
                px: 2,
                '&:hover': {
                  bgcolor: 'rgba(183, 28, 28, 0.08)',
                  borderColor: '#B71C1C',
                },
              }}
            >
              Fechamento (PDF)
            </Button>
          )}
          {onExportExcel && (
            <Button
              variant='contained'
              size='small'
              startIcon={<Iconify icon='vscode-icons:file-type-excel' width={16} />}
              onClick={onExportExcel}
              disabled={!hasData}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                backgroundColor: '#1B5E20',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 1.5,
                px: 2,
                '&:hover': { backgroundColor: '#2E7D32' },
                '&:disabled': {
                  backgroundColor: 'rgba(145, 158, 171, 0.12)',
                  color: 'text.disabled',
                },
              }}
            >
              Exportar Excel
            </Button>
          )}
        </Stack>
      )}
    </Box>
  )
}

export const SelectEvents = ({ onFilters, filters }: { onFilters: any; filters: any }) => {
  const [query, setQuery] = useState('')
  const searchDebounce = useDebounce(query, 500)

  const { isLoading, isFetching, data } = useEvents(
    {
      page: 1,
      perPage: 100,
      sort: undefined,
    },
    {
      search: searchDebounce,
    },
  )

  const handleFilterService = useCallback(
    (event: SyntheticEvent<Element, Event>, selectedValue: OptionType | null) => {
      onFilters('event', selectedValue)
      if (!selectedValue) {
        onFilters('categories', null)
      }
    },
    [onFilters],
  )

  return (
    <FormControl
      sx={{
        width: '100%',
      }}
    >
      <Autocomplete
        id='event-list'
        size='small'
        options={data?.data?.map((option) => ({ label: option?.name, value: option?._id })) || []}
        isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
        getOptionLabel={(option: OptionType | string) => (option as OptionType)?.label}
        onChange={handleFilterService}
        value={filters.event}
        loading={isLoading || isFetching}
        inputValue={query}
        onInputChange={(event, newInputValue) => {
          setQuery(newInputValue)
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            {option.label}
          </li>
        )}
        renderInput={(params) => <TextField label='Eventos' {...params} />}
      />
    </FormControl>
  )
}

export const SelectCategory = ({ onFilters, filters }: { onFilters: any; filters: any }) => {
  const [query, setQuery] = useState('')
  const [data, setData] = useState<OptionType[]>([]) // Define a type if you have one
  const searchDebounce = useDebounce(query, 500)

  useEffect(() => {
    const fetchData = async () => {
      const result = await getCategoriesToAutocomplete(searchDebounce, filters.event?.value)
      // @ts-ignore
      setData(result || [])
    }

    fetchData()
  }, [searchDebounce, filters.event?.value])

  const handleFilterService = useCallback(
    (event: SyntheticEvent<Element, Event>, selectedValue: OptionType | null) => {
      onFilters('categories', selectedValue)
    },
    [onFilters],
  )

  return (
    <FormControl
      sx={{
        width: '100%',
      }}
    >
      <Autocomplete
        id='category-list'
        size='small'
        // @ts-ignore
        options={data || []}
        isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
        getOptionLabel={(option: OptionType | string) => (option as OptionType)?.label}
        // @ts-ignore
        onChange={handleFilterService}
        inputValue={query}
        onInputChange={(event, newInputValue) => {
          setQuery(newInputValue)
        }}
        renderInput={(params) => <TextField label='Categorias' {...params} />}
      />
    </FormControl>
  )
}

export const SelectVendedores = ({ onFilters, filters }: { onFilters: any; filters: any }) => {
  const [query, setQuery] = useState('')
  const searchDebounce = useDebounce(query, 500)

  const { isLoading, isFetching, data } = useUsers(
    {
      page: 1,
      perPage: 100,
      sort: undefined,
    },
    {
      search: searchDebounce,
    },
  )

  const handleFilterService = useCallback(
    (event: SyntheticEvent<Element, Event>, selectedValues: any[]) => {
      onFilters('salers', selectedValues)
    },
    [onFilters],
  )

  return (
    <FormControl
      sx={{
        flexShrink: 0,
        width: '100%',
      }}
    >
      <Autocomplete
        id='event-list'
        multiple
        size='small'
        options={data?.data?.map((option) => ({ label: option?.name, value: option?._id })) || []}
        isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
        getOptionLabel={(option: OptionType | string) => (option as OptionType)?.label}
        onChange={handleFilterService}
        value={filters.salers}
        loading={isLoading || isFetching}
        inputValue={query}
        onInputChange={(event, newInputValue) => {
          setQuery(newInputValue)
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            {option.label}
          </li>
        )}
        renderInput={(params) => <TextField label='Vendedores' {...params} />}
      />
    </FormControl>
  )
}
